/**
 * Utility functions để xác định trạng thái công nợ
 */

export interface DebtStatusInfo {
  totalDebt: number;
  totalPaid: number;
  remain: number;
  status: 'unpaid' | 'partial' | 'paid';
}

/**
 * Xác định trạng thái công nợ dựa trên số tiền nợ và đã trả
 * @param totalDebt - Tổng số tiền nợ
 * @param totalPaid - Tổng số tiền đã trả
 * @returns Thông tin trạng thái công nợ
 */
export function calculateDebtStatus(totalDebt: number, totalPaid: number): DebtStatusInfo {
  const remain = totalDebt - totalPaid;
  
  let status: 'unpaid' | 'partial' | 'paid';
  
  if (remain <= 0) {
    // Nếu số tiền còn lại <= 0, coi như đã trả hết
    status = 'paid';
  } else if (totalPaid > 0) {
    // Nếu đã trả một phần
    status = 'partial';
  } else {
    // Chưa trả gì
    status = 'unpaid';
  }
  
  return {
    totalDebt,
    totalPaid,
    remain: Math.max(0, remain), // Đảm bảo remain không âm
    status
  };
}

/**
 * Xác định trạng thái công nợ cho một danh sách các khoản nợ
 * @param debts - Danh sách các khoản nợ
 * @returns Thông tin trạng thái tổng hợp
 */
export function calculateGroupedDebtStatus(debts: Array<{ debt_amount: number; paid_amount: number }>): DebtStatusInfo {
  const totalDebt = debts.reduce((sum, debt) => sum + debt.debt_amount, 0);
  const totalPaid = debts.reduce((sum, debt) => sum + debt.paid_amount, 0);
  
  return calculateDebtStatus(totalDebt, totalPaid);
}

/**
 * Lấy màu sắc cho trạng thái công nợ
 * @param status - Trạng thái công nợ
 * @returns Màu sắc tương ứng
 */
export function getDebtStatusColor(status: 'unpaid' | 'partial' | 'paid'): string {
  switch (status) {
    case 'paid':
      return 'green';
    case 'partial':
      return 'orange';
    case 'unpaid':
      return 'red';
    default:
      return 'default';
  }
}

/**
 * Lấy text hiển thị cho trạng thái công nợ
 * @param status - Trạng thái công nợ
 * @returns Text hiển thị tương ứng
 */
export function getDebtStatusText(status: 'unpaid' | 'partial' | 'paid'): string {
  switch (status) {
    case 'paid':
      return 'Đã trả';
    case 'partial':
      return 'Còn nợ';
    case 'unpaid':
      return 'Chưa trả';
    default:
      return 'Không xác định';
  }
}
